import { TabBar, type TabBarProps } from '@adornis/components/client/TabBar.js';

export interface PageTabItem<T extends string> {
  key: T;
  label: string;
}

/**
 * The page-level tab strip that sits in the page header: full width, lifted, so the active tab reads as a folder
 * tab that opens into the page below it.
 *
 * The header slot keeps the strip it was given, so pass `activeKey` as a getter reading the router: the strip then
 * follows navigation that is no tab click (a link, the title) and its DOM stays, which the transition needs.
 */
export const PageTabs = <T extends string>(props: {
  tabs: PageTabItem<T>[];
  activeKey: TabBarProps['activeID'];
  onSelect: (key: T) => void;
}) =>
  TabBar({
    variant: 'lift',
    className: 'w-full',
    // body size, not DaisyUI's text-sm: these are page-level navigation under a text-xl title
    tabClassName: 'text-base',
    items: props.tabs.map(tab => ({ id: tab.key, label: tab.label })),
    activeID: props.activeKey,
    onActiveChange: id => {
      const tab = props.tabs.find(candidate => candidate.key === id);
      if (tab) props.onSelect(tab.key);
    },
  });
